import React, { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, Box, TablePagination, Button, TextField, Dialog, DialogTitle, DialogContent, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemText
} from '@mui/material';
import AddSubscriptionDialog from './AddSubscriptionDialog';
import OndemandWashDialog from './OndemandWashDialog';
import { useNavigate, Link } from 'react-router-dom';

const SubscriptionList = ({expiring=false}) => {
  const [subs, setSubs] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [addOpen, setAddOpen] = useState(false);
  const [ondemandOpen, setOndemandOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [carRegNo, setCarRegNo] = useState('');
  const [statusFilter, setStatusFilter] = useState(['active']);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [userModalLoading, setUserModalLoading] = useState(false);
  const [userModalData, setUserModalData] = useState(null);
  // pause modal state
  const [pauseOpen, setPauseOpen] = useState(false);
  const [pauseSubId, setPauseSubId] = useState(null);
  const [pauseUntil, setPauseUntil] = useState('');
  // grace days modal state & refresh counter
  const [graceOpen, setGraceOpen] = useState(false);
  const [graceSubId, setGraceSubId] = useState(null);
  const [graceDays, setGraceDays] = useState('');
  const [graceLoading, setGraceLoading] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    let url = `${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/list?page=${page + 1}&limit=${rowsPerPage}`;
    if (search) url += `&searchRegex=${encodeURIComponent(search)}`;
    if (carRegNo) url += `&carRegNoRegex=${encodeURIComponent(carRegNo)}`;
    if (Array.isArray(statusFilter) && statusFilter.length > 0 && statusFilter.includes('all')) {
      url += `&status=${encodeURIComponent(['active', 'cancelled'].join(','))}`;
    }

     if (Array.isArray(statusFilter) && statusFilter.length > 0 && !statusFilter.includes('all')) {
      url += `&status=${encodeURIComponent(statusFilter.join(','))}`;
    }

    fetch(url, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(result => {
        console.log('Fetched subscriptions:', result.data);
        setSubs(result.data || []);
        setTotal(result.total || 0);
      })
      .catch(() => {
        setSubs([]);
        setTotal(0);
      });
  }, [page, rowsPerPage, search, carRegNo, statusFilter, refreshCounter]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDelete = (subId) => {
    if (!window.confirm('Are you sure you want to delete this subscription?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/delete/${subId}`, {
      method: 'DELETE',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(() => {
        setSubs(subs => subs.filter(s => s._id !== subId));
        setTotal(t => t - 1);
      })
      .catch(() => {
        alert('Failed to delete subscription.');
      });
  };

  const handleCustomerNameClick = (sub) => {
    setUserModalOpen(true);
    setUserModalLoading(true);
    setUserModalData(null);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/users/${sub.userId}`, {
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(data => {
        setUserModalData(data);
        setUserModalLoading(false);
      })
      .catch(() => {
        setUserModalData(null);
        setUserModalLoading(false);
      });
  };

  const handleStop = (subId) => {
    if (!window.confirm('Are you sure you want to Stop this subscription?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptions/stop/${subId}`, {
      method: 'DELETE',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(() => {
        setSubs(subs => subs.map(s => s._id === subId ? { ...s, status: 'Paused' } : s));
      })
      .catch(() => {
        alert('Failed to pause subscription.');
      });
  };

  // open pause modal (choose future date)
  const handlePauseOpen = (subId) => {
    setPauseSubId(subId);
    setPauseUntil('');
    setPauseOpen(true);
  };

  const handlePauseCancel = () => {
    setPauseOpen(false);
    setPauseSubId(null);
    setPauseUntil('');
  };

  const handlePauseSave = async () => {
    if (!pauseSubId) return;
    if (!pauseUntil) {
      alert('Please select a future date.');
      return;
    }

    // pauseUntil is a date-only string (YYYY-MM-DD).
    // Create a local Date at end of that day (23:59:59.999) so pause covers the whole day.
    const [y, m, d] = pauseUntil.split('-').map(Number);
    if (!y || !m || !d) {
      alert('Invalid date selected.');
      return;
    }
    const selected = new Date(y, m - 1, d, 23, 59, 59, 999);
    const now = new Date();
    if (selected <= now) {
      alert('Please select a future date.');
      return;
    }

    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`https://cmw-backend-a2ao.onrender.com/customer/pauseSubscription/${pauseSubId}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ pauseUntilDate: selected.toISOString() }),
      });
      await res.json();
      setSubs(subs => subs.map(s => s._id === pauseSubId ? { ...s, status: 'Paused', pauseUntil: selected.toISOString() } : s));
      handlePauseCancel();
    } catch (err) {
      console.error(err);
      alert('Failed to pause subscription.');
    }
  };

  const handleSkip = (subId) => {
    if (!window.confirm('Are you sure you want to Skip this subscription?')) return;
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    fetch(`https://cmw-backend-a2ao.onrender.com/customer/skipWash/${subId}`, {
      method: 'POST',
      headers: {
        Authorization: token ? `Bearer ${token}` : '',
      },
    })
      .then(res => res.json())
      .then(() => {
        setSubs(subs => subs.map(s => s._id === subId ? { ...s, status: 'Skipped' } : s));
      })
      .catch(() => {
        alert('Failed to skip subscription.');
      });
  };

  // open add grace days modal
  const handleAddGraceDays = (subId) => {
    setGraceSubId(subId);
    setGraceDays('');
    setGraceOpen(true);
  };

  const handleGraceCancel = () => {
    setGraceOpen(false);
    setGraceSubId(null);
    setGraceDays('');
    setGraceLoading(false);
  };

  const handleGraceSubmit = async () => {
    if(!window.confirm('Add grace days to this subscription?')) return;
    if (!graceSubId) return;
    const days = parseInt(graceDays, 10);
    if (!days || days <= 0) {
      alert('Please enter a valid number of days.');
      return;
    }
    setGraceLoading(true);
    const token = JSON.parse(localStorage.getItem('auth_token_crm'))?.token;
    try {
      const res = await fetch(`${process.env.REACT_APP_SERVER_URI}/subscriptionGroups/addGrace/${graceSubId}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ days })
      });
      if (!res.ok) throw new Error(`Failed (${res.status})`);
      // optionally inspect response
      setRefreshCounter(c => c + 1); // trigger refetch in useEffect
      handleGraceCancel();
      alert('Grace days added successfully.');
    } catch (err) {
      console.error(err);
      alert('Failed to add grace days.');
      setGraceLoading(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ flexGrow: 1 }}>Subscription Management</Typography>
        <TextField
          label="Search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          size="small"
          sx={{ mr: 2, minWidth: 200 }}
        />
        <TextField
          label="Car Reg No"
          value={carRegNo}
          onChange={e => setCarRegNo(e.target.value)}
          size="small"
          sx={{ mr: 2, minWidth: 180 }}
        />
        <FormControl size="small" sx={{ mr: 2, minWidth: 180 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            multiple
            value={statusFilter}
            label="Status"
            onChange={e => {
              const value = e.target.value;
              if (Array.isArray(value) && value.includes('all')) {
                setStatusFilter(['all']);
              } else {
                setStatusFilter(Array.isArray(value) ? value : [value]);
              }
            }}
            renderValue={(selected) => Array.isArray(selected) ? selected.join(', ') : selected}
          >
            <MenuItem value="active">
              <Checkbox checked={statusFilter.includes('active')} />
              <ListItemText primary="Active" />
            </MenuItem>
            <MenuItem value="cancelled">
              <Checkbox checked={statusFilter.includes('cancelled')} />
              <ListItemText primary="Cancelled" />
            </MenuItem>
          </Select>
        </FormControl>
        <Button variant="contained" onClick={() => setAddOpen(true)} sx={{ mr: 2 }}>Add Subscription</Button>
        <Button variant="outlined" onClick={() => setOndemandOpen(true)} sx={{ mr: 2 }}>Ondemand Wash</Button>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Customer Name</TableCell>
              <TableCell>Plan</TableCell>
              <TableCell>Scheduled</TableCell>
              <TableCell>Start Date</TableCell>
              <TableCell>Expire Date</TableCell>
              <TableCell>Price</TableCell>
              <TableCell>Renewed on</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {subs.map(sub => (
              <TableRow key={sub._id}>
                <TableCell>
                  <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => handleCustomerNameClick(sub)}
                  >
                    {(sub.userId?.name || '-') + ' (' + (sub.carId?.regNo || '-') + ')'}
                  </Button>
                </TableCell>
                <TableCell>
                <Button
                    variant="text"
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                    onClick={() => navigate(`/subscription/${sub._id}`)}
                  >
                    {sub.currentSubscriptionPlan || '-'}
                  </Button>
                </TableCell>
                <TableCell>
                    {Array.isArray(sub.schedule) ? sub.schedule.length > 0
                      ? sub.currentSchedule.map(s =>
                          typeof s === 'object' && s.day
                            ? s.day
                            : typeof s === 'string'
                              ? s
                              : '-'
                        ).join(', ')
                      : '-': sub.currentSchedule?.exterior.map(s =>s.day).join(', ')}
                </TableCell>
                <TableCell>
                  {sub.startDate
                    ? new Date(sub.startDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>
                  {sub.expireDate
                    ? new Date(sub.expireDate).toLocaleDateString()
                    : '-'}
                </TableCell>
                <TableCell>{sub.currentSubscriptionPlanPrice || 0}</TableCell>
                <TableCell>{new Date(sub.lastRenewalDate).toLocaleDateString() || '-'}</TableCell>
                <TableCell>{sub.status || '-'}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleAddGraceDays(sub._id)}
                    >
                      Add grace days
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handlePauseOpen(sub._id)}
                    >
                      Pause
                    </Button>
                    {/* <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleSkip(sub._id)}
                    >
                      Skip
                    </Button> */}
                    Comming soon....
                    {/* <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleDelete(sub._id)}
                    >
                      Delete
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleStop(sub._id)}
                    >
                      Stop
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handlePauseOpen(sub._id)}
                    >
                      Pause
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      color="error"
                      onClick={() => handleSkip(sub._id)}
                    >
                      Skip
                    </Button> */}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
        />
      </TableContainer>
      {/* Add Grace Days Dialog */}
      <Dialog open={graceOpen} onClose={handleGraceCancel}>
        <DialogTitle>Add Grace Days</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              label="Number of days"
              type="number"
              value={graceDays}
              onChange={e => setGraceDays(e.target.value)}
              fullWidth
              InputProps={{ inputProps: { min: 1 } }}
              sx={{ mt: 1 }}
            />
            <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
              <Button onClick={handleGraceCancel} disabled={graceLoading}>Cancel</Button>
              <Button variant="contained" onClick={handleGraceSubmit} disabled={graceLoading}>
                {graceLoading ? <CircularProgress size={20} /> : 'Add'}
              </Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
      <Dialog open={addOpen} onClose={()=> setAddOpen(false)} fullWidth>
          <DialogTitle>Add Subscription</DialogTitle>
          <AddSubscriptionDialog
            open={addOpen}
            onClose={() => setAddOpen(false)}
            onAdded={() => setPage(0)}
          />
      </Dialog>
      <OndemandWashDialog
        open={ondemandOpen}
        onClose={() => setOndemandOpen(false)}
      />
      <Dialog open={userModalOpen} onClose={() => setUserModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>User & Car Details</DialogTitle>
        <DialogContent>
          {userModalLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : userModalData ? (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>User Details</Typography>
              <Typography>Name: {userModalData.name || '-'}</Typography>
              <Typography>Email: {userModalData.email || '-'}</Typography>
              <Typography>Phone: {userModalData.phone || '-'}</Typography>
              <Typography>Address: {userModalData.address || '-'}</Typography>
              <Typography>Notes: {userModalData.notes || '-'}</Typography>
              <Box mt={2}>
                <Typography variant="h6" sx={{ mb: 1 }}>Cars</Typography>
                {Array.isArray(userModalData.cars) && userModalData.cars.length > 0 ? (
                  userModalData.cars.map((car, idx) => (
                    <Box key={idx} sx={{ mb: 2, p: 1, border: '1px solid #eee', borderRadius: 1 }}>
                      <Typography>Car Name: {car.name || '-'}</Typography>
                      <Typography>Model: {car.model || '-'}</Typography>
                      <Typography>Reg No: {car.regNo || '-'}</Typography>
                      <Typography>Car Type: {car.type || '-'}</Typography>
                      <Typography>Street: {car.location || '-'}</Typography>
                      <Typography>Address: {car.address || '-'}</Typography>
                      <Typography>Notes: {car.notes || '-'}</Typography>
                      <Typography>
                        Google Maps:{' '}
                        {Array.isArray(car.gps) && car.gps.length === 2
                          ? (
                            <a
                              href={`https://www.google.com/maps?q=${car.gps[0]},${car.gps[1]}`}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {`[${car.gps[0]}, ${car.gps[1]}]`}
                            </a>
                          )
                          : '-'}
                      </Typography>
                      <Typography>
                        Car Free Time: {Array.isArray(car.carFreeTime) && car.carFreeTime.length > 0
                          ? car.carFreeTime.map((ft, i) =>
                              `${ft.day || '-'} ${ft.from || '-'}-${ft.to || '-'}`
                            ).join(', ')
                          : '-'}
                      </Typography>
                    </Box>
                  ))
                ) : (
                  <Typography>No cars found.</Typography>
                )}
              </Box>
            </Box>
          ) : (
            <Typography>No user data found.</Typography>
          )}
        </DialogContent>
      </Dialog>
      {/* Pause Subscription Dialog */}
      <Dialog open={pauseOpen} onClose={handlePauseCancel}>
        <DialogTitle>Pause Subscription</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1, minWidth: 300 }}>
            <TextField
              label="Pause Until (date)"
              type="date"
              value={pauseUntil}
              onChange={e => setPauseUntil(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              sx={{ mt: 1 }}
            />
            <Box display="flex" justifyContent="flex-end" gap={1} sx={{ mt: 2 }}>
              <Button onClick={handlePauseCancel}>Cancel</Button>
              <Button variant="contained" onClick={handlePauseSave}>Pause</Button>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default SubscriptionList;
